import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  { n: "01", text: "The Brief" },
  { n: "02", text: "The Concept" },
  { n: "03", text: "Visual Treatment" },
  { n: "04", text: "Character & Environment Build" },
  { n: "05", text: "Animation & Movement" },
  { n: "06", text: "Voice-Overs" },
  { n: "07", text: "The Montage" },
  { n: "08", text: "Sound Design" },
  { n: null, text: "A Highly Polished Advertising Film.", final: true },
];

const STEP_MS = 2100;
const FINAL_MS = 4500;

export default function Pipeline() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const current = STEPS[index];
    const id = setTimeout(
      () => setIndex((i) => (i + 1) % STEPS.length),
      current.final ? FINAL_MS : STEP_MS
    );
    return () => clearTimeout(id);
  }, [index]);

  const step = STEPS[index];

  return (
    <section
      id="pipeline"
      data-testid="pipeline-section"
      className="border-t border-white/10 px-6 py-28 md:px-10 md:py-40"
    >
      <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <span className="font-mono text-[10px] tracking-[0.45em] text-zinc-500">
            THE PIPELINE
          </span>
          <h2 className="mt-4 font-serif text-4xl leading-[1.15] tracking-tight sm:text-5xl">
            Relax. We do this the same old way.
          </h2>
        </motion.div>

        <div className="mt-16 flex flex-col items-center md:mt-20">
          <div className="flex h-5 items-center justify-center overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={`label-${index}`}
                data-testid="pipeline-step-label"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="font-mono text-[10px] tracking-[0.45em] text-zinc-500"
              >
                {step.final ? "THE RESULT" : `STEP ${step.n} / 08`}
              </motion.span>
            </AnimatePresence>
          </div>

          <div
            data-testid="pipeline-rotating"
            aria-live="polite"
            className="relative mt-4 w-full font-serif text-4xl leading-[1.2] tracking-tight sm:text-5xl lg:text-6xl"
          >
            <span className="invisible block px-4" aria-hidden="true">
              A Highly Polished Advertising Film.
            </span>
            <AnimatePresence mode="wait">
              <motion.span
                key={index}
                data-testid="pipeline-rotating-line"
                className="absolute inset-0 overflow-hidden"
              >
                <motion.span
                  className={`flex h-full items-center justify-center px-4 pb-[0.25em] ${
                    step.final ? "italic text-[#E1A730]" : "text-white"
                  }`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "-110%" }}
                  transition={{ duration: 0.65, ease: EASE }}
                >
                  {step.text}
                </motion.span>
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-14 font-mono text-[10px] tracking-[0.4em] text-zinc-600"
        >
          NOTHING CHANGES — EXCEPT THE CREW SIZE
        </motion.p>
      </div>
    </section>
  );
}
